import { createContext, useContext, type JSX } from 'react';
import {
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical';
import { rowReference, storedTableReference } from '@erp/workbooks';
import {
  FormulaReferenceChip,
  FormulaTableReferenceChip,
} from './FormulaReferenceChip';
import {
  resolveFormulaField,
  resolveFormulaTable,
  type FormulaFields,
} from './worksheetFormulaFields';

/** The Fields the Formula being edited may reference; chips resolve against them. */
export const FormulaFieldsContext = createContext<FormulaFields>({
  inputName: '',
  inputIcon: 'data',
  fields: [],
  complete: false,
});

function EditorReferenceChip({ name }: { name: string }) {
  const fields = useContext(FormulaFieldsContext);
  return (
    <FormulaReferenceChip
      name={name}
      field={resolveFormulaField(fields, name)}
    />
  );
}

type SerializedFormulaReferenceNode = Spread<
  { name: string },
  SerializedLexicalNode
>;

/**
 * One reference in the Formula editor. It is atomic: Backspace removes it
 * whole, and its text (what copy puts on the clipboard) is the Row reference.
 */
export class FormulaReferenceNode extends DecoratorNode<JSX.Element> {
  __name: string;

  static getType(): string {
    return 'workbook-formula-reference';
  }

  static clone(node: FormulaReferenceNode): FormulaReferenceNode {
    return new FormulaReferenceNode(node.__name, node.__key);
  }

  static importJSON(
    serialized: SerializedFormulaReferenceNode,
  ): FormulaReferenceNode {
    return $createFormulaReferenceNode(serialized.name);
  }

  constructor(name: string, key?: NodeKey) {
    super(key);
    this.__name = name;
  }

  exportJSON(): SerializedFormulaReferenceNode {
    return { ...super.exportJSON(), name: this.__name };
  }

  getName(): string {
    return this.getLatest().__name;
  }

  createDOM(): HTMLElement {
    return document.createElement('span');
  }

  updateDOM(): false {
    return false;
  }

  isInline(): true {
    return true;
  }

  getTextContent(): string {
    return rowReference(this.__name);
  }

  decorate(): JSX.Element {
    return <EditorReferenceChip name={this.__name} />;
  }
}

export function $createFormulaReferenceNode(
  name: string,
): FormulaReferenceNode {
  return new FormulaReferenceNode(name);
}

export function $isFormulaReferenceNode(
  node: LexicalNode | null | undefined,
): node is FormulaReferenceNode {
  return node instanceof FormulaReferenceNode;
}

function EditorTableReferenceChip({
  tableId,
  column,
}: {
  tableId: string;
  column: string | null;
}) {
  const fields = useContext(FormulaFieldsContext);
  return (
    <FormulaTableReferenceChip
      table={resolveFormulaTable(fields, tableId)}
      column={column}
    />
  );
}

type SerializedFormulaTableReferenceNode = Spread<
  { tableId: string; column: string | null },
  SerializedLexicalNode
>;

/**
 * One Table reference in the Formula editor: another table's column (or the
 * whole table, as VLOOKUP reads it), by the table's id. Its text is the stored
 * reference, so a rename never changes what it reads; copy writes the
 * table's current name instead (see `$copiedFormulaText`).
 */
export class FormulaTableReferenceNode extends DecoratorNode<JSX.Element> {
  __tableId: string;
  __column: string | null;

  static getType(): string {
    return 'workbook-formula-table-reference';
  }

  static clone(node: FormulaTableReferenceNode): FormulaTableReferenceNode {
    return new FormulaTableReferenceNode(
      node.__tableId,
      node.__column,
      node.__key,
    );
  }

  static importJSON(
    serialized: SerializedFormulaTableReferenceNode,
  ): FormulaTableReferenceNode {
    return $createFormulaTableReferenceNode(
      serialized.tableId,
      serialized.column,
    );
  }

  constructor(tableId: string, column: string | null, key?: NodeKey) {
    super(key);
    this.__tableId = tableId;
    this.__column = column;
  }

  exportJSON(): SerializedFormulaTableReferenceNode {
    return {
      ...super.exportJSON(),
      tableId: this.__tableId,
      column: this.__column,
    };
  }

  createDOM(): HTMLElement {
    return document.createElement('span');
  }

  updateDOM(): false {
    return false;
  }

  isInline(): true {
    return true;
  }

  getTextContent(): string {
    return storedTableReference(this.__tableId, this.__column);
  }

  decorate(): JSX.Element {
    return (
      <EditorTableReferenceChip
        tableId={this.__tableId}
        column={this.__column}
      />
    );
  }
}

export function $createFormulaTableReferenceNode(
  tableId: string,
  column: string | null,
): FormulaTableReferenceNode {
  return new FormulaTableReferenceNode(tableId, column);
}
